import { FormApi } from "final-form";
import _ from "lodash";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Certificate, Partition } from "src/types/osbuilder";
import { useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { FileContentType } from "@dashboard/files/types";
import { createSource } from "@dashboard/products/components/ProductBuilder/components/Recipes";
import { AndroidModule, Product } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { Tooltip } from "@shared/Tooltip";

import SharedUtils from "../ModulePanel.utils";

import Utils from "./AddApplicationDialog.utils";

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

type FormValues = {
    type: "prebuilt_app";
    fileId: string;
    partition: Partition;
    privileged: "regular" | "privileged";
    certificate: Certificate;
};
type Props = {
    product: Product | null;
    open: boolean;
    onToggle: (value: boolean) => void;
};

export const AddApplicationDialog = (props: Props) => {
    const validate = useValidationSchema(Utils.schema);
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "modals", "common", "sidepanels"]);

    const [updateProduct] = useUpdateProductMutation();

    const modules = props.product?.customBuildConfig?.modules || [];
    const sources = props.product?.customBuildConfig?.sources || {};

    const { data: workspace } = useCurrentWorkspace();
    const { data } = useAllCurrentWorkspaceFiles(
        { contentTypes: [FileContentType.Apk, FileContentType.ApkV2] },
        { skip: !props.open },
    );

    const accessibleFileOptions = data.filter((item) => !SharedUtils.checkFileSourceExists(modules, sources, item.id));
    const fileOptions = _.orderBy(
        accessibleFileOptions.map((item) => ({ label: item.filename, value: item.id })),
        "label",
    );

    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const formDisabled = !fileOptions.length || cannotManageProducts || !props.product;

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const file = data.find((item) => item.id === values.fileId);

        if (file && props.product) {
            const [sourceId, newSource] = createSource(file, sources);

            const newModule: AndroidModule = {
                type: "prebuilt_app",
                name: file.filename,
                sourceId,
                partition: values.partition,
                privileged: values.privileged === "privileged",
                certificate: values.certificate,
                tags: ["Own"],
            };

            const newProduct = _.cloneDeep(props.product);
            if (newProduct.customBuildConfig) {
                newProduct.customBuildConfig.modules = [...modules, newModule];
                newProduct.customBuildConfig.sources = { ...sources, ...newSource };
            }

            await updateProduct(newProduct);
        }
        form.restart();
        props.onToggle(false);
    };
    const getTooltipTitle = () => {
        if (!fileOptions.length) {
            return t("modals:manageProductModules.addApplication.noFilesAvailable");
        }
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!props.product) {
            return t("common:tooltip.noProductSelected");
        }
        return undefined;
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{
                partition: "product",
                privileged: "regular",
                certificate: "presigned",
                type: "prebuilt_app",
                fileId: fileOptions[0]?.value,
            }}
            render={({ handleSubmit, form, submitting }) => (
                <Modal
                    open={props.open}
                    onClose={() => props.onToggle(false)}
                    title={t("modals:manageProductModules.addApplication.title")}
                    description={t("modals:manageProductModules.addApplication.description")}
                    endButton={
                        <Tooltip hide={!formDisabled} title={getTooltipTitle()}>
                            <PrimaryButton onClick={form.submit} disabled={submitting || formDisabled} loading={submitting}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        </Tooltip>
                    }
                >
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <ControlledSelectField
                            name="fileId"
                            options={fileOptions}
                            disabled={!fileOptions.length}
                            label={
                                fileOptions.length
                                    ? t("common:label.file")
                                    : t("modals:manageProductModules.addApplication.noFilesAvailable")
                            }
                        />
                        <ControlledSelectField
                            name="partition"
                            label={t("modals:manageProductModules.addApplication.input.partition.caption")}
                            options={SharedUtils.partitionOptions}
                        />
                        <ControlledSelectField
                            name="privileged"
                            label={t("modals:manageProductModules.addApplication.input.privileged.caption")}
                            options={Utils.privilegedOptions}
                        />
                        <ControlledSelectField
                            name="certificate"
                            label={t("modals:manageProductModules.addApplication.input.certificate.caption")}
                            options={Utils.certificateOptions}
                        />
                    </form>
                </Modal>
            )}
        />
    );
};
